import { useCallback } from 'react';
import { toast } from 'sonner';
import { usePwaInstallPrompt } from '@/hooks/usePwaInstallPrompt';
import { isRunningStandalone } from '@/utils/pwaInstall';

/**
 * One place for "install the app" so the footer, the Home banner and the menu
 * all behave the same: the browser's own install prompt when there is one,
 * otherwise a short hint on how to add it by hand.
 */
export function useInstallApp() {
  const { canInstall, install } = usePwaInstallPrompt();
  const isInstalled = isRunningStandalone();

  const handleInstall = useCallback(async () => {
    if (canInstall) {
      await install();
      return;
    }
    if (isRunningStandalone()) {
      toast.info('The app is already installed on this device.');
      return;
    }
    // No prompt to show (iPhone Safari never has one, and other browsers hold
    // it back until the page has been used a little) — say how to add it.
    if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
      toast.info('Tap the Share button, then choose "Add to Home Screen".');
      return;
    }
    toast.info('Open your browser menu (⋮) and tap "Install app" or "Add to Home screen".');
  }, [canInstall, install]);

  return { isInstalled, handleInstall };
}
